import React from 'react';

export type AnalyticsSummary = {
  count: number;
  averages: {
    environment: number;
    social: number;
    governance: number;
    total: number;
  };
  distribution: Record<'red' | 'yellow' | 'green', number>;
  benchmarks: Array<{ group: string; average: number; count: number }>;
  trend: Array<{ date: number; total: number; level: string }>;
  filtersApplied: {
    sector?: string;
    region?: string;
    employeeBand?: string;
    from?: number;
    to?: number;
  };
};

export function AnalyticsSummaryPanel({ summary }: { summary: AnalyticsSummary }) {
  const total = summary.distribution.red + summary.distribution.yellow + summary.distribution.green || 1;
  const distributionBars = [
    { label: 'Red', value: summary.distribution.red, color: 'bg-red-500/70' },
    { label: 'Yellow', value: summary.distribution.yellow, color: 'bg-yellow-400/70' },
    { label: 'Green', value: summary.distribution.green, color: 'bg-green-400/70' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Environment" value={summary.averages.environment} suffix="/12" />
        <StatCard label="Social" value={summary.averages.social} suffix="/10" />
        <StatCard label="Governance" value={summary.averages.governance} suffix="/8" />
        <StatCard label="Total" value={summary.averages.total} suffix="/30" highlight />
      </div>

      <div>
        <div className="text-xs uppercase tracking-wide text-neutral-400 mb-2">Distribution</div>
        <div className="flex h-3 rounded overflow-hidden bg-neutral-800 border border-neutral-700">
          {distributionBars.map((bar) => (
            <div
              key={bar.label}
              className={`${bar.color} transition-all`}
              style={{ width: `${((bar.value || 0) / total) * 100}%` }}
              title={`${bar.label}: ${bar.value}`}
            />
          ))}
        </div>
        <div className="flex justify-between text-xs text-neutral-500 mt-1">
          {distributionBars.map((bar) => (
            <span key={bar.label}>{bar.label}: {bar.value}</span>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wide text-neutral-400 mb-2">Benchmarks</div>
        {summary.benchmarks.length === 0 ? (
          <div className="text-xs text-neutral-500 border border-neutral-800 rounded p-3">No benchmark groups yet.</div>
        ) : (
          <div className="space-y-2">
            {summary.benchmarks.map((bench) => (
              <div key={bench.group} className="flex items-center justify-between border border-neutral-800 rounded px-3 py-2 text-sm">
                <div className="text-neutral-300">{bench.group}</div>
                <div className="text-neutral-100 font-semibold">{bench.average} <span className="text-xs text-neutral-500">({bench.count})</span></div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, suffix, highlight }: { label: string; value: number; suffix?: string; highlight?: boolean }) {
  return (
    <div className={`rounded-lg border ${highlight ? 'border-evat-neon/50 bg-evat-neon/5' : 'border-neutral-800 bg-neutral-900/40'} p-3`}>
      <div className="text-xs uppercase tracking-wide text-neutral-400">{label}</div>
      <div className={`text-2xl font-orbitron ${highlight ? 'text-evat-neon' : 'text-neutral-100'}`}>
        {value.toFixed(1)}
        {suffix && <span className="text-sm font-light text-neutral-500 ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

